import get from 'lodash.get';
import { useContext } from 'react';
import PropTypes from 'prop-types';
import { createFragmentContainer, graphql } from 'react-relay/legacy';
import { connect } from 'react-redux';
import { FormattedMessage, defineMessages, useIntl } from 'dibs-react-intl';

// components
import RowContainer from '../../shared/CheckoutRowContainer';
import CheckoutEmailLink from '../CheckoutEmailLink';
import CheckoutPhoneLink from '../CheckoutPhoneLink';
import CheckoutWireTransferForm from './CheckoutWireTransferForm';

//context
import { CheckoutServerVarsContext } from '../../components/shared/CheckoutServerVarsContext';

import styles from './styles/CheckoutWireTransferWrapper.scss';

const lineBreak = '\r\n';

const messages = defineMessages({
    subject: {
        id: 'checkout.CheckoutWireTransferWrapper.email.subject',
        defaultMessage: 'Request Payment by Wire Transfer ({dibsReference})',
    },
    greeting: {
        id: 'checkout.CheckoutWireTransferWrapper.email.greeting',
        defaultMessage: 'Dear 1stDibs Trade Relations,',
    },
    line1: {
        id: 'checkout.CheckoutWireTransferWrapper.email.line1',
        defaultMessage:
            'I would like to purchase {title} with a wire transfer and have it shipped to:',
    },
    line2: {
        id: 'checkout.CheckoutWireTransferWrapper.email.line2',
        defaultMessage:
            'Please have a 1stDibs representative contact me to complete this transaction. The best way to reach me is ____________.',
    },
    line3: {
        id: 'checkout.CheckoutWireTransferWrapper.email.line3',
        defaultMessage: 'Customer ID: {id}',
    },
});

const CheckoutWireTransferWrapper = ({
    item: { title, dibsReference },
    buyer,
    transaction,
    tradeStatus,
    viewer,
    shippingAddressId,
    wrapperClass = '',
    relay,
}) => {
    const { isMobile } = useContext(CheckoutServerVarsContext);
    const intl = useIntl();

    function getFormattedAddress(address = {}) {
        const { displayName, company, formattedAddress, displayCountry, phoneNumber } = address;
        const arr = [
            displayName,
            company,
            ...formattedAddress.map(r => r),
            displayCountry,
            phoneNumber,
        ];
        return arr.filter(r => r).join(lineBreak);
    }
    function getEmailArgs() {
        const isVerifiedTrade = get(buyer, 'isVerifiedTrade');
        const bodyArr = isVerifiedTrade ? [intl.formatMessage(messages.greeting)] : [];
        const buyerId = get(buyer, 'serviceId');
        const address = (get(buyer, 'addressBook.addressList') || []).find(
            a => get(a, 'serviceId') === shippingAddressId
        );

        bodyArr.push(
            intl.formatMessage(messages.line1, { title }),
            address ? getFormattedAddress(address) : '',
            intl.formatMessage(messages.line2),
            intl.formatMessage(messages.line3, { id: buyerId })
        );

        return {
            subject: intl.formatMessage(messages.subject, { dibsReference }),
            body: bodyArr.join(lineBreak + lineBreak),
        };
    }
    function getCopy() {
        const { subject, body } = getEmailArgs();
        const isVerifiedTrade = get(buyer, 'isVerifiedTrade');
        const isNegotiable = get(transaction, 'isNegotiable');
        const phone = (
            <CheckoutPhoneLink viewer={viewer} hasTradeStatus={tradeStatus.hasTradeStatus} />
        );
        const emailLink = (
            <CheckoutEmailLink address="payments@1stdibs.com" subject={subject} body={body} />
        );
        if (isVerifiedTrade && isNegotiable) {
            return {
                line1: (
                    <FormattedMessage
                        id="checkout.CheckoutWireTransferWrapper.tradeIsNegotiable.line1"
                        defaultMessage="To pay directly from your bank account, complete checkout, and one of our Trade specialists will reach out with next steps."
                    />
                ),
                line2: (
                    <FormattedMessage
                        id="checkout.CheckoutWireTransferWrapper.tradeIsNegotiable.line2"
                        defaultMessage="Your bank may charge a fee for the transaction. ACH transfer and electronic checks are not forms of wire transfer and cannot be processed."
                    />
                ),
                line3: (
                    <FormattedMessage
                        id="checkout.CheckoutWireTransferWrapper.tradeIsNegotiable.line3"
                        defaultMessage="Questions? Call 1stDibs at {phone} from 9am – 5pm ET or email {emailLink}. We'll help set up your payment details and send your offer to the seller."
                        values={{ emailLink, phone }}
                    />
                ),
            };
        } else if (isVerifiedTrade) {
            return {
                line1: (
                    <FormattedMessage
                        id="checkout.CheckoutWireTransferWrapper.trade.line1"
                        defaultMessage="To pay directly from your bank account, complete checkout, and one of our Trade specialists will reach out with next steps."
                    />
                ),
                line2: (
                    <FormattedMessage
                        id="checkout.CheckoutWireTransferWrapper.trade.line2"
                        defaultMessage="Your bank may charge a fee for the transaction. ACH transfer and electronic checks are not forms of wire transfer and cannot be processed."
                    />
                ),
                line3: (
                    <FormattedMessage
                        id="checkout.CheckoutWireTransferWrapper.trade.line3"
                        defaultMessage="Questions? Call 1stDibs at {phone} from 9am – 5pm ET or email {emailLink}."
                        values={{ emailLink, phone }}
                    />
                ),
            };
        } else if (get(buyer, 'isVip') || get(transaction, 'isPrivateOffer')) {
            return {
                line1: (
                    <FormattedMessage
                        id="checkout.CheckoutWireTransferWrapper.vipOrPrivateOffer.line1"
                        defaultMessage="We provide the option to pay directly from your bank account with a wire transfer. After you check out online, a 1stDibs representative will reach out with next steps."
                    />
                ),
                line2: (
                    <FormattedMessage
                        id="checkout.CheckoutWireTransferWrapper.vipOrPrivateOffer.line2"
                        defaultMessage="Please note that your bank may charge a fee for this transaction. ACH transfer and electronic checks are not forms of wire transfer and cannot be processed."
                    />
                ),
                line3: (
                    <FormattedMessage
                        id="checkout.CheckoutWireTransferWrapper.vipOrPrivateOffer.line3"
                        defaultMessage="If you have questions about payment by wire transfer please email us at {emailLink} or call 1stDibs at {phone}."
                        values={{ emailLink, phone }}
                    />
                ),
            };
        } else if (isNegotiable) {
            return {
                line1: (
                    <FormattedMessage
                        id="checkout.CheckoutWireTransferWrapper.defaultNegotiable.line1"
                        defaultMessage="To pay directly from your bank account, call 1stDibs {phone} from 9am – 5pm ET or email {emailLink}. We'll help set up your payment details and send your offer to the seller."
                        values={{ emailLink, phone }}
                    />
                ),
                line2: null,
                line3: null,
            };
        }
        return {
            line1: (
                <FormattedMessage
                    id="checkout.CheckoutWireTransferWrapper.defaultFullPrice.line1"
                    defaultMessage="To pay directly from your bank account, call 1stDibs at {phone} from 9am – 5pm ET or email {emailLink}. We'll help set up your payment details."
                    values={{ emailLink, phone }}
                />
            ),
            line2: null,
            line3: null,
        };
    }

    const { line1, line2, line3 } = getCopy() || {};

    return (
        <div className={wrapperClass}>
            <RowContainer
                noMargin
                isClickable={false}
                noHorizontalPadding={isMobile}
                wrapperClass={styles.rowContainerWrapper}
            >
                <p className={styles.information}>{line1}</p>
                {!!line2 && <p className={styles.information}>{line2}</p>}
                <p className={styles.information} data-tn="wire-support-info">
                    {line3}
                </p>
            </RowContainer>
            <CheckoutWireTransferForm viewer={viewer} buyer={buyer} relay={relay} />
        </div>
    );
};

CheckoutWireTransferWrapper.propTypes = {
    item: PropTypes.object.isRequired,
    buyer: PropTypes.object.isRequired,
    transaction: PropTypes.object.isRequired,
    wrapperClass: PropTypes.string,
    shippingAddressId: PropTypes.string.isRequired,
    viewer: PropTypes.object.isRequired,
    tradeStatus: PropTypes.object.isRequired,
    relay: PropTypes.object,
};

const ConnectedCheckoutWireTransferWrapper = connect(({ transaction, tradeStatus }) => ({
    shippingAddressId: transaction.shippingAddressId,
    tradeStatus,
}))(CheckoutWireTransferWrapper);

export default createFragmentContainer(ConnectedCheckoutWireTransferWrapper, {
    viewer: graphql`
        fragment CheckoutWireTransferWrapper_viewer on Viewer {
            ...CheckoutPhoneLink_viewer
            ...CheckoutWireTransferForm_viewer
        }
    `,
    item: graphql`
        fragment CheckoutWireTransferWrapper_item on Item {
            title
            dibsReference
        }
    `,
    buyer: graphql`
        fragment CheckoutWireTransferWrapper_buyer on User {
            serviceId
            isVip
            isVerifiedTrade: hasPermission(name: "B_NET_PRICE")
            addressBook {
                addressList {
                    serviceId
                    displayName
                    company
                    formattedAddress
                    displayCountry
                    phoneNumber
                }
            }
            ...CheckoutWireTransferForm_buyer
        }
    `,
    transaction: graphql`
        fragment CheckoutWireTransferWrapper_transaction on Transaction {
            isNegotiable
            isPrivateOffer
        }
    `,
});
